import {
  GetIndexHistories200Response,
  GetMarkets200Response,
  GetMarketsSummary200ResponseSummariesInner,
  InstrumentTypeResponse,
} from "../../codegen-api";
import { spotDailyChange } from "../../components/CommandModal/CommandModal.sections";
import { IPerpetualMarketData } from "../../components/CommandModal/MarketsTable";
import { getConfigPricePrecision } from "../../components/CommandModal/utils";
import { IMarket } from "../../contexts/MarketContext";
import { SpotStatisticsByAsset } from "../../hooks/api/statistics/useGetSpotStatistics";
import { AssetResponse } from "../../utils/asset";
import { instrumentLabel } from "../../utils/instrumentLabel";
import { knownPrice } from "../../utils/unknownNumbers";
import { apiNum } from "../../utils/math";
import { normalizeCategory } from "./helpers";
import { CategoryEnum } from "./types";

export type WatchlistRow = IPerpetualMarketData & {
  category: CategoryEnum;
  instrumentType: InstrumentTypeResponse;
};

/** The vertical a watchlist row belongs to; perpetual for legacy rows. */
export const rowDerivative = (row: IPerpetualMarketData) =>
  row.instrumentType ?? InstrumentTypeResponse.Perpetual;

/** Same entry: same asset AND same vertical (BTC-USD is not BTC-USDC). */
export const isSameEntry = (
  entry: IMarket,
  row: { asset: string; instrumentType?: InstrumentTypeResponse }
) =>
  entry.asset === row.asset &&
  entry.derivative === (row.instrumentType ?? InstrumentTypeResponse.Perpetual);

/**
 * One row per watchlist entry, in the watchlist's saved order, for every
 * vertical that has rows to show. `useWatchlist` already drops entries of a
 * switched-off vertical, so a spot-only build lists its spot entries here —
 * the page used to keep `derivative === Perpetual` only, and a starred
 * BTC-USDC never appeared.
 *
 * Perp rows read `/markets-summary`; spot rows read the spot market list and
 * `/statistics` (the trading header's source). An entry whose market has not
 * loaded is left out rather than shown with invented numbers.
 */
export function buildWatchlistRows({
  watchlist,
  summaries,
  perpMarkets,
  spotMarkets,
  spotStatistics,
  market,
  indexHistories,
}: {
  watchlist: IMarket[];
  summaries?: GetMarketsSummary200ResponseSummariesInner[];
  perpMarkets?: GetMarkets200Response[];
  spotMarkets?: GetMarkets200Response[];
  spotStatistics?: SpotStatisticsByAsset;
  market: IMarket;
  indexHistories?: GetIndexHistories200Response;
}): WatchlistRow[] {
  const summaryByAsset = new Map(
    (summaries || []).map((s) => [s.asset as string, s])
  );
  const perpByAsset = new Map(
    (perpMarkets || []).map((m) => [m.underlying_asset, m])
  );
  const spotByAsset = new Map(
    (spotMarkets || []).map((m) => [m.underlying_asset, m])
  );
  const isCurrent = (asset: string, derivative: InstrumentTypeResponse) =>
    market.asset === asset && market.derivative === derivative;

  return watchlist
    .map((w): WatchlistRow | null => {
      if (w.derivative === InstrumentTypeResponse.Perpetual) {
        const s = summaryByAsset.get(w.asset);
        const m = perpByAsset.get(w.asset);
        if (!s) return null;
        // Same rule as the command palette (CommandModal.sections): a change
        // needs a real 24h reference price. `mark - change` always "succeeded"
        // (an absent mark made it -change) and an absent change was a flat 0%.
        const change = apiNum(s.perpetual_info?.mark_daily_change);
        const price24hAgo = apiNum(
          (s.perpetual_info as any)?.mark_price_24h_ago
        );
        const dailyChange =
          change !== undefined && price24hAgo !== undefined && price24hAgo > 0
            ? change / price24hAgo
            : undefined;
        const isPrelaunch =
          !!s.perpetual_info?.pre_launch ||
          (s as any).market_category === "Pre-IPO";
        return {
          id: `PERPETUAL-${w.asset}`,
          asset: w.asset as AssetResponse,
          market: instrumentLabel(w.asset, InstrumentTypeResponse.Perpetual),
          price: s.perpetual_info?.mark_price ?? undefined,
          dailyChange,
          dailyVolume: apiNum(s.perpetual_info?.daily_volume),
          isWatchlist: true,
          isPrelaunch,
          category: normalizeCategory(
            (m as any)?.market_category || (s as any).market_category,
            isPrelaunch
          ),
          instrumentType: InstrumentTypeResponse.Perpetual,
          isCurrentMarket: isCurrent(w.asset, InstrumentTypeResponse.Perpetual),
          pricePrecision: getConfigPricePrecision(
            w.asset as AssetResponse,
            s.perpetual_info?.amount_step,
            s.perpetual_info?.price_step
          ),
          indexHistory:
            indexHistories?.assets_history?.find((h) => h.asset === w.asset)
              ?.history || [],
        };
      }
      if (w.derivative === InstrumentTypeResponse.Spot) {
        const m = spotByAsset.get(w.asset);
        if (!m) return null;
        const stats = spotStatistics?.[w.asset];
        // Price from the polled /statistics mark first: /markets is fetched once (immutable),
        // so its mark_price is the price at page load while the % beside it moves.
        const statsMark = stats?.mark_price as string | undefined;
        const price =
          knownPrice(statsMark) !== undefined
            ? String(statsMark)
            : m.mark_price || undefined;
        return {
          id: `SPOT-${w.asset}`,
          asset: w.asset as AssetResponse,
          market: instrumentLabel(w.asset, InstrumentTypeResponse.Spot),
          price,
          dailyChange: spotDailyChange(stats, price),
          dailyVolume: apiNum(stats?.daily_volume as any),
          isWatchlist: true,
          isPrelaunch: false,
          category: normalizeCategory(m.market_category, false),
          instrumentType: InstrumentTypeResponse.Spot,
          isCurrentMarket: isCurrent(w.asset, InstrumentTypeResponse.Spot),
          pricePrecision: getConfigPricePrecision(
            w.asset,
            m.amount_step,
            m.price_step
          ),
          indexHistory: [],
        };
      }
      // Options entries have no row shape on this page.
      return null;
    })
    .filter((x): x is WatchlistRow => x !== null);
}

/**
 * Move one of `subset` (entries shown in some list, in watchlist order) from
 * `fromIdx` to `toIdx`, leaving every entry outside the subset where it was.
 */
export function reorderWatchlistSubset(
  watchlist: IMarket[],
  subset: IMarket[],
  fromIdx: number,
  toIdx: number
): IMarket[] {
  if (fromIdx === toIdx) return watchlist;
  const moved = subset[fromIdx];
  const target = subset[toIdx];
  if (!moved || !target) return watchlist;
  const same = (a: IMarket) => (b: IMarket) =>
    a.asset === b.asset && a.derivative === b.derivative;
  const fromGlobal = watchlist.findIndex(same(moved));
  const targetGlobal = watchlist.findIndex(same(target));
  if (fromGlobal < 0 || targetGlobal < 0) return watchlist;
  const next = [...watchlist];
  const [item] = next.splice(fromGlobal, 1);
  // Dragging down lands after the target; dragging up, before it.
  next.splice(targetGlobal, 0, item);
  return next;
}
